import React, { useState } from "react";
import { Button, Col, Form, message, Modal, Row } from "antd";

import { guard } from "utils/general";
import { formatToMomentObject, formatDateDisplay } from "utils/date";

import FormAutoComplete from "components/FormAutoComplete/FormAutoComplete";
import FormDatePicker from "components/FormDatePicker/FormDatePicker";
import FormInput from "components/FormInput/FormInput";
import Locale from "react-intl-universal";

const { useForm } = Form;
const { confirm } = Modal;

const getActionPlanSuggestions = () => {
  const areaSelections = ["Cashflow", "Insurance", "Will", "Investment"];
  return guard(
    () =>
      areaSelections.map((selection) => ({
        label: selection,
        key: selection,
        value: selection,
      })),
    []
  );
};

const AddActionPlanModal = ({
  visible,
  onOk,
  onCancel,
  onRemove,
  defaultValues,
}) => {
  const [form] = useForm();
  const [selections, setSelections] = useState([]);

  const getInitialValues = () => {
    if (defaultValues) {
      return {
        ...defaultValues,
        actionDate: formatToMomentObject(defaultValues.actionDate),
      };
    }
  };

  const handleOnOkButtonClick = async (e) => {
    e.preventDefault();
    try {
      const values = await form.validateFields();

      const formattedValues = {
        ...values,
        actionDate: formatDateDisplay(values.actionDate),
      };

      onOk(formattedValues);
    } catch (error) {
      error.errorFields.forEach((field) => message.error(field.errors[0]));
    }
  };

  const handleOnRemoveButtonClick = () => {
    confirm({
      title: Locale.get("actionPlan.confirmRemove"),
      okText: Locale.get("actionPlan.yes"),
      onOk() {
        onRemove();
      },
      onClose() {},
    });
  };

  const handleOnSearchActionPlans = (inputValue) => {
    const currentResourceSuggestions = getActionPlanSuggestions();
    const newSelections = inputValue
      ? currentResourceSuggestions.filter((suggestion) => {
          const upperCaseSuggestionValue = suggestion.value.toUpperCase();
          return upperCaseSuggestionValue.includes(inputValue.toUpperCase());
        })
      : [];

    setSelections(newSelections);
  };

  const footer = defaultValues
    ? [
        <Button key="remove" onClick={handleOnRemoveButtonClick}>
          {Locale.get("actionPlan.removePlan")}
        </Button>,
        <Button key="submit" type="primary" onClick={handleOnOkButtonClick}>
          {Locale.get("actionPlan.savePlan")}
        </Button>,
      ]
    : [
        <Button key="cancel" onClick={onCancel}>
          {Locale.get("actionPlan.cancel")}
        </Button>,
        <Button key="submit" type="primary" onClick={handleOnOkButtonClick}>
          {Locale.get("actionPlan.addNewPlan")}
        </Button>,
      ];

  return (
    <Modal
      title={Locale.get("actionPlan.actionPlanDetail")}
      visible={visible}
      onOk={handleOnOkButtonClick}
      onCancel={onCancel}
      footer={footer}
    >
      <Form form={form} initialValues={getInitialValues()}>
        <Row gutter={[16, 4]}>
          <Col span={24}>
            <FormAutoComplete
              label={Locale.get("actionPlan.areas")}
              name="area"
              requiredErrorMessage={Locale.get("actionPlan.errMsgAreas")}
              placeholder={Locale.get("actionPlan.areaPlaceholder")}
              selections={selections}
              onSearch={handleOnSearchActionPlans}
              defaultActiveFirstOption
            />
          </Col>
          <Col span={24}>
            <FormInput
              label={Locale.get("actionPlan.suggestedAction")}
              name="action"
              extraProps={{
                onPressEnter: handleOnOkButtonClick,
              }}
            />
          </Col>
          <Col span={12}>
            <FormInput
              label={Locale.get("actionPlan.initiator")}
              name="initiator"
              extraProps={{
                onPressEnter: handleOnOkButtonClick,
              }}
            />
          </Col>
          <Col span={12}>
            <FormDatePicker
              label={Locale.get("actionPlan.actionDate")}
              name="actionDate"
            />
          </Col>
          <Col span={24}>
            <FormInput
              label={Locale.get("actionPlan.decision")}
              name="decision"
              extraProps={{
                onPressEnter: handleOnOkButtonClick,
              }}
              type="textArea"
            />
          </Col>
        </Row>
      </Form>
    </Modal>
  );
};

export default AddActionPlanModal;
